import { api, unwrap, getToken, type APIResponse } from "./client";

// Lead capture (F6): what a rep learns about the person on a call, kept.
//
// Agent side mirrors app/routers/agent.py's LeadBundle / LeadSave
// (GET/PUT /api/agent/lead). Supervisor side mirrors app/routers/lead.py
// (/api/leads). Names and numbers on the supervisor side arrive MASKED when
// the account's PII rule says so (`masked` on the list); the agent side is
// never masked -- the agent is talking to the person.

export type LeadFieldType =
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "boolean"
  | "date"
  | "email"
  | "phone";

export interface LeadFieldSpec {
  key: string;
  label: string;
  type: LeadFieldType;
  options: string[];
  required: boolean;
  // Hidden on the supervisor's view when the account is masked.
  pii: boolean;
  placeholder: string | null;
}

export type LeadValues = Record<string, string | number | boolean>;

export interface LeadCapture {
  spool_id: number;
  first_name: string | null;
  last_name: string | null;
  fields: LeadValues;
  agent_user_id: number | null;
  updated_at: string | null;
}

/** The most recent capture for this NUMBER under the account, from an
 * earlier call. Offered as a pre-fill; nothing is written back unless the rep
 * edits something. */
export interface LeadPrevious {
  spool_id: number;
  first_name: string | null;
  last_name: string | null;
  fields: LeadValues;
  agent_name: string | null;
  campaign_name: string | null;
  at: string | null;
}

export interface AgentLeadBundle {
  spool_id: number;
  campaign_id: number | null;
  phone: string;
  form: LeadFieldSpec[];
  capture: LeadCapture | null;
  previous: LeadPrevious | null;
}

export interface LeadSaveBody {
  spool_id: number;
  first_name: string | null;
  last_name: string | null;
  fields: LeadValues;
  // The seat the form is open on. The server binds the row to it (the call
  // must have popped on, or be live on, that seat). Optional for an agent
  // token, which carries its seat; required for an operator on the shared
  // login, exactly like /current-call.
  extension_id?: number | null;
}

export async function getAgentLead(spoolId: number, extensionId: number | null = null): Promise<AgentLeadBundle> {
  const resp = await api.get<APIResponse<AgentLeadBundle>>("/agent/lead", {
    params: extensionId == null ? { spool_id: spoolId } : { spool_id: spoolId, extension_id: extensionId },
  });
  return unwrap(resp.data);
}

export async function saveAgentLead(body: LeadSaveBody): Promise<LeadCapture> {
  const resp = await api.put<APIResponse<LeadCapture>>("/agent/lead", body);
  return unwrap(resp.data);
}

/**
 * Best-effort save fired from `pagehide` / `beforeunload`, the same way the
 * console's logout beacon works: `fetch(..., {keepalive: true})` survives the
 * page going away, and unlike sendBeacon it can carry the Authorization
 * header. Fire-and-forget -- the localStorage draft is the backstop, and
 * `flushLeadDrafts` replays it the next time a console mounts.
 */
export function saveAgentLeadKeepalive(body: LeadSaveBody): void {
  const token = getToken();
  try {
    void fetch("/api/agent/lead", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // best-effort -- see doc comment
  }
}

// ---------------------------------------------------------------------------
// Supervisor side
// ---------------------------------------------------------------------------

export interface LeadDetail {
  key: string;
  label: string;
  value: string | number | boolean | null;
}

export interface LeadRow {
  lead_capture_id: number;
  spool_id: number;
  campaign_id: number | null;
  campaign_name: string | null;
  agent_user_id: number | null;
  agent_name: string | null;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  details: LeadDetail[];
  disposition: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeadList {
  items: LeadRow[];
  total: number;
  page: number;
  per_page: number;
  masked: boolean;
  timezone: string;
  date_from: string;
  date_to: string;
}

export interface LeadListParams {
  date_from?: string;
  date_to?: string;
  campaign_id?: number;
  agent_user_id?: number;
  q?: string;
  page?: number;
  per_page?: number;
}

export async function listLeads(params: LeadListParams): Promise<LeadList> {
  // Trailing slash: the route is defined at "/" and FastAPI 307-redirects
  // "/leads" -> "/leads/", which can drop the auth header through the proxy.
  const resp = await api.get<APIResponse<LeadList>>("/leads/", { params });
  return unwrap(resp.data);
}

export interface LeadPreset {
  id: string;
  label: string;
  fields: LeadFieldSpec[];
}

export interface LeadForm {
  campaign_id: number;
  fields: LeadFieldSpec[];
  // False when the campaign shows the default set (no row of its own).
  customised: boolean;
}

export async function getLeadPresets(): Promise<LeadPreset[]> {
  const resp = await api.get<APIResponse<LeadPreset[]>>("/leads/presets");
  return unwrap(resp.data);
}

export async function getLeadForm(campaignId: number): Promise<LeadForm> {
  const resp = await api.get<APIResponse<LeadForm>>(`/leads/fields/${campaignId}`);
  return unwrap(resp.data);
}

export type LeadFormUpdate = { fields: LeadFieldSpec[] } | { preset: string } | { reset: true };

export async function saveLeadForm(campaignId: number, body: LeadFormUpdate): Promise<LeadForm> {
  const resp = await api.put<APIResponse<LeadForm>>(`/leads/fields/${campaignId}`, body);
  return unwrap(resp.data);
}
